/**
 * Google Identity Services — obtain an access token (popup UX).
 * Used to refresh expired tokens before Gmail / Drive / Classroom calls.
 *
 * Mirrors the link-time scope list so a silent token refresh can never
 * silently downgrade the granted scope set. Google treats
 * `classroom.coursework.students` and `classroom.coursework.students.readonly`
 * as DISTINCT scopes — never assume one implies the other. Same goes
 * for the `me` / `me.readonly` pair.
 */
export const GOOGLE_APP_SCOPES = [
  'openid',
  'email',
  'profile',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/classroom.courses',
  'https://www.googleapis.com/auth/classroom.courses.readonly',
  'https://www.googleapis.com/auth/classroom.rosters.readonly',
  'https://www.googleapis.com/auth/classroom.profile.emails',
  'https://www.googleapis.com/auth/classroom.coursework.students',
  'https://www.googleapis.com/auth/classroom.coursework.students.readonly',
  'https://www.googleapis.com/auth/classroom.coursework.me',
  'https://www.googleapis.com/auth/classroom.coursework.me.readonly',
  'https://www.googleapis.com/auth/classroom.student-submissions.students.readonly',
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/spreadsheets',
].join(' ');

type TokenResponse = { access_token?: string; error?: string; error_description?: string };

type TokenClient = { requestAccessToken: (overrideConfig?: { prompt?: '' | 'consent' }) => void };

type InitTokenClient = (config: {
  client_id: string;
  scope: string;
  ux_mode?: 'popup' | 'redirect';
  callback: (resp: TokenResponse) => void;
  error_callback?: (err: { type?: string }) => void;
}) => TokenClient;

export function requestGoogleAccessToken(options: {
  clientId: string;
  /** '' tries a silent refresh when Google already has consent */
  prompt?: '' | 'consent';
}): Promise<string | null> {
  const { clientId, prompt = '' } = options;
  return new Promise((resolve) => {
    const oauth2 = (
      window as Window & {
        google?: { accounts?: { oauth2?: { initTokenClient: InitTokenClient } } };
      }
    ).google?.accounts?.oauth2;

    if (!oauth2?.initTokenClient) {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      resolve(token);
    };

    const timeoutId = window.setTimeout(() => finish(null), 120_000);

    try {
      const client = oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_APP_SCOPES,
        ux_mode: 'popup',
        callback: (resp: TokenResponse) => {
          window.clearTimeout(timeoutId);
          if (resp.access_token) finish(resp.access_token);
          else finish(null);
        },
        error_callback: () => {
          window.clearTimeout(timeoutId);
          finish(null);
        },
      });
      client.requestAccessToken({ prompt });
    } catch {
      window.clearTimeout(timeoutId);
      finish(null);
    }
  });
}

/** Heuristics for Gmail/Drive 401-style failures */
export function isGoogleAuthErrorMessage(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('invalid authentication credentials') ||
    m.includes('invalid credentials') ||
    m.includes('invalid_grant') ||
    m.includes('login required') ||
    m.includes('unauthorized') ||
    m.includes('401') ||
    m.includes('access token') ||
    m.includes('token has been expired') ||
    m.includes('token expired') ||
    m.includes('auth error')
  );
}
